import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMessageCircle } from "react-icons/fi";
import EmojiPicker from "emoji-picker-react";
import { useLocation } from "react-router-dom";

// Styles
import "./MessagingCenter.css";

// Components
import ChatSidebar from "./ChatSidebar";
import ChatHeader from "./ChatHeader";
import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";
import NewMessageModal from "./NewMessageModal";
import MessageMenu from "./MessageMenu";
import ReportModal from "./ReportModal";
import Message from "./Message";
import MessageSearchModal from "./MessageSearchModal";

// Services
import messagingService from "../../services/messagingService";
import messagingSocketService from "../../services/messagingSocketService";
import encryptionManager from "../../services/encryptionManager";
import messagingCacheService from "../../services/messagingCacheService";

// Hooks
import useDirectMessageJump from "../../hooks/useDirectMessageJump";

// Context
import { useUserContext } from "../../context/UserContext";
import { useNotificationContext } from "../../context/NotificationContext"; // NEW: Import NotificationContext

// Toast
import { toast } from "react-toastify";

// Animation variants for stagger effects
const messagesContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
};

// ============================================
// DATE FORMATTING UTILITIES (from Backend Guide)
// ============================================

const formatMessageDate = (date) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time for comparison
  const messageDateOnly = new Date(messageDate.toDateString());
  const todayOnly = new Date(today.toDateString());
  const yesterdayOnly = new Date(yesterday.toDateString());

  if (messageDateOnly.getTime() === todayOnly.getTime()) {
    return "Today";
  }

  if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
    return "Yesterday";
  }

  // Within last 7 days - show day name
  const daysDiff = Math.floor(
    (todayOnly - messageDateOnly) / (1000 * 60 * 60 * 24)
  );
  if (daysDiff < 7) {
    return messageDate.toLocaleDateString("en-US", { weekday: "long" });
  }

  // Same year - show month and day
  if (messageDate.getFullYear() === today.getFullYear()) {
    return messageDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  }

  // Different year - show full date
  return messageDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getDateKey = (date) => {
  // Returns YYYY-MM-DD for grouping
  return new Date(date).toISOString().split("T")[0];
};

/**
 * ⚠️ IMPORTANT: Backend returns messages in CHRONOLOGICAL order (oldest first)
 * Messages are already sorted by createdAt ASC
 */
const groupMessagesByDate = (messages) => {
  const groups = {};

  // ⚠️ DEDUPLICATE: Remove any duplicate messages by _id before grouping
  const seen = new Set();
  const uniqueMessages = messages.filter((msg) => {
    if (seen.has(msg._id)) return false;
    seen.add(msg._id);
    return true;
  });

  // Messages come oldest first (chronological order from backend)
  uniqueMessages.forEach((message) => {
    const dateKey = getDateKey(message.createdAt);

    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: message.createdAt,
        dateKey,
        label: formatMessageDate(message.createdAt),
        messages: [],
      };
    }

    groups[dateKey].messages.push(message);
  });

  // Convert to array - already in chronological order (oldest first)
  return Object.values(groups).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
};

// Helper: Show avatar only for first message in a sequence from same sender
const shouldShowAvatar = (messages, index) => {
  if (index === 0) return true;
  return messages[index].senderId !== messages[index - 1].senderId;
};

// Helper: Show time only for last message in a sequence or after 5 min gap
// eslint-disable-next-line no-unused-vars
const shouldShowTime = (messages, index) => {
  if (index === messages.length - 1) return true;

  const currentTime = new Date(messages[index].createdAt);
  const nextTime = new Date(messages[index + 1].createdAt);
  const diffMinutes = (nextTime - currentTime) / (1000 * 60);

  // Show time if next message is > 5 minutes later or from different sender
  return (
    diffMinutes > 5 || messages[index].senderId !== messages[index + 1].senderId
  );
};

const MessagingCenter = () => {
  const { user } = useUserContext();
  const { clearMessagingUnread } = useNotificationContext(); // NEW: Get clearMessagingUnread function
  const location = useLocation();

  // State management
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [conversationFilter, setConversationFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state for infinite scroll - ⚠️ Backend uses hasNext NOT hasMore!
  const [pagination, setPagination] = useState({
    hasNext: true, // ⚠️ Backend field name (NOT hasMore)
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    isLoading: false,
    isLoadingMore: false,
  });

  // Scroll position ref for maintaining position after loading older messages
  const prevScrollHeightRef = useRef(0);
  const justLoadedOlderMessagesRef = useRef(false); // Track if we just loaded older messages

  // 🎯 Jump-to-message functionality
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);

  // UI state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  // Message actions state
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingMessageId, setReportingMessageId] = useState(null);
  const [longPressMenuMessageId, setLongPressMenuMessageId] = useState(null);

  // Typing indicators
  const [typingUsers, setTypingUsers] = useState(new Map());
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false); // Track if we're currently typing (for debounce)

  // Unread counts
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByConversation, setUnreadByConversation] = useState({});

  // Online status tracking
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  // Message cache to avoid re-fetching
  const [messageCache, setMessageCache] = useState({});

  // E2EE state
  const [isE2EEEnabled, setIsE2EEEnabled] = useState(false);
  const [encryptionReady, setEncryptionReady] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const selectedConversationRef = useRef(null);

  // ============================================
  // MESSAGE FORMATTING (moved before hooks that need it)
  // ============================================

  const formatMessage = useCallback((message) => {
    const currentUserId = user?.id || user?._id;
    const messageSenderId =
      message.sender?._id || message.sender?.id || message.sender;
    const isMine = currentUserId && messageSenderId === currentUserId;

    return {
      ...message,
      senderId: isMine ? "me" : messageSenderId,
      serverId: message.serverId || message._id, // Preserve serverId or use _id
      status: {
        sending: message.status?.sending || false,
        sent: message.status?.sent !== false, // Default to true for server messages
        delivered: message.status?.delivered || false,
        deliveredAt: message.status?.deliveredAt,
        read: message.status?.read || false,
        readAt: message.status?.readAt,
        failed: message.status?.failed || false,
      },
      type: message.messageType || "text",
    };
  }, [user]);

  // 🎯 Jump-to-message hook
  const {
    jumpedMessages,
    targetMessageId,
    targetIndex,
    hasOlder: jumpHasOlder,
    hasNewer: jumpHasNewer,
    isJumpedMode,
    isJumping,
    isLoadingOlder: jumpLoadingOlder,
    isLoadingNewer: jumpLoadingNewer,
    jumpToMessage,
    loadOlder: loadOlderJumped,
    loadNewer: loadNewerJumped,
    exitJumpedMode,
    clearHighlight,
  } = useDirectMessageJump(
    selectedConversation?._id,
    formatMessage,
    user?.id || user?._id
  );

  // Keep selectedConversationRef in sync
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
    setTypingUsers(new Map());
  }, [selectedConversation]);

  // ============================================
  // INITIALIZATION & SOCKET CONNECTION
  // ============================================

  useEffect(() => {
    // Hydrate cache from localStorage for instant display
    const hydrated = messagingCacheService.hydrate();

    // If we have cached conversations, show them immediately
    if (hydrated.conversations) {
      const cachedConversations = messagingCacheService.getConversations();
      if (cachedConversations?.conversations) {
        setConversations(cachedConversations.conversations);
}
    }

    // If we have cached unread counts, show them immediately
    if (hydrated.unreadCount) {
      const cachedUnread = messagingCacheService.getUnreadCount();
      if (cachedUnread) {
        setTotalUnread(cachedUnread.totalUnread || 0);
        setUnreadByConversation(cachedUnread.byConversation || {});
      }
    }

    initializeEncryption();
    messagingSocketService.connect();

    // Fetch fresh data in background (stale-while-revalidate pattern)
    fetchConversationsWithPresence();
    fetchUnreadCount();

    // Also fetch online contacts
    fetchOnlineContacts();

    // Handle visibility change - force refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchConversationsWithPresence();

        // If a conversation is selected, refresh its messages
        if (selectedConversationRef.current) {
          fetchMessages(selectedConversationRef.current._id, null, true); // forceRefresh=true
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      messagingSocketService.disconnect();
      encryptionManager.clearCache();
      // Note: We don't clear messaging cache on unmount - that's the point!
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle navigation from other pages
  useEffect(() => {
    if (location.state?.selectedConversation) {
      const conversation = location.state.selectedConversation;
      setConversations((prev) => {
        const exists = prev.find((c) => c._id === conversation._id);
        if (!exists) {
          return [conversation, ...prev];
        }
        return prev;
      });
      handleConversationSelect(conversation);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ============================================
  // SOCKET EVENT HANDLERS
  // ============================================

  useEffect(() => {
    // Core message events
    messagingSocketService.on("new_message", handleNewMessage);
    messagingSocketService.on("message_sent", handleMessageSent);
    messagingSocketService.on("message_error", handleMessageError);
    messagingSocketService.on("message_delivered", handleMessageDelivered);
    messagingSocketService.on("messages_delivered", handleMessagesDelivered);
    messagingSocketService.on("messages_read", handleMessagesRead);
    messagingSocketService.on("message_deleted", handleMessageDeleted);
    messagingSocketService.on("message_edited", handleMessageEdited);

    // Reaction events
    messagingSocketService.on("reaction_added", handleReactionAdded);
    messagingSocketService.on("reaction_removed", handleReactionRemoved);

    // Typing events
    messagingSocketService.on("user_typing", handleUserTyping);
    messagingSocketService.on("user_stopped_typing", handleUserStoppedTyping);

    // Presence events
    messagingSocketService.on("user_online", handleUserOnline);
    messagingSocketService.on("user_offline", handleUserOffline);
    messagingSocketService.on("user_presence_update", handleUserPresenceUpdate);
    messagingSocketService.on("online_contacts_list", handleOnlineContactsList);

    // Connection events
    messagingSocketService.on(
      "connection_established",
      handleConnectionEstablished
    );
    messagingSocketService.on("sync_complete", handleSyncComplete);

    const handleSocketReconnect = () => {
      if (selectedConversationRef.current) {
        messagingSocketService.joinConversation(
          selectedConversationRef.current._id
        );
      }
    };
    messagingSocketService.on("socket_connected", handleSocketReconnect);

    return () => {
      messagingSocketService.off("new_message", handleNewMessage);
      messagingSocketService.off("message_sent", handleMessageSent);
      messagingSocketService.off("message_error", handleMessageError);
      messagingSocketService.off("message_delivered", handleMessageDelivered);
      messagingSocketService.off("messages_delivered", handleMessagesDelivered);
      messagingSocketService.off("messages_read", handleMessagesRead);
      messagingSocketService.off("message_deleted", handleMessageDeleted);
      messagingSocketService.off("message_edited", handleMessageEdited);
      messagingSocketService.off("reaction_added", handleReactionAdded);
      messagingSocketService.off("reaction_removed", handleReactionRemoved);
      messagingSocketService.off("user_typing", handleUserTyping);
      messagingSocketService.off(
        "user_stopped_typing",
        handleUserStoppedTyping
      );
      messagingSocketService.off("user_online", handleUserOnline);
      messagingSocketService.off("user_offline", handleUserOffline);
      messagingSocketService.off(
        "user_presence_update",
        handleUserPresenceUpdate
      );
      messagingSocketService.off(
        "online_contacts_list",
        handleOnlineContactsList
      );
      messagingSocketService.off(
        "connection_established",
        handleConnectionEstablished
      );
      messagingSocketService.off("sync_complete", handleSyncComplete);
      messagingSocketService.off("socket_connected", handleSocketReconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewMessage = async (data) => {
    const { message } = data;
    if (!message) return;

    let processedMessage = message;
    if (message.isEncrypted && message.encryptedContent) {
      try {
        processedMessage = await encryptionManager.processReceivedMessage(
          message,
          message.conversation
        );
      } catch (error) {
        processedMessage = {
          ...message,
          content: "🔒 [Decryption failed]",
          error: true,
        };
      }
    }

    const currentUserId = user?.id || user?._id;
    const messageSenderId =
      processedMessage.sender?._id ||
      processedMessage.sender?.id ||
      processedMessage.sender;
    const isMyMessage = currentUserId && messageSenderId === currentUserId;

    // Skip socket event for own messages - they're already handled by optimistic update
    if (isMyMessage) {
      return;
    }

    if (
      selectedConversationRef.current &&
      processedMessage.conversation === selectedConversationRef.current._id
    ) {
      const formattedMsg = formatMessage(processedMessage);

      setMessages((prev) => {
        const existsByMessageId = prev.some(
          (msg) => msg._id === processedMessage._id
        );
        if (existsByMessageId) {
          return prev;
        }
        return [...prev, formattedMsg];
      });

      // Update persistent cache with new message
      messagingCacheService.addMessage(
        processedMessage.conversation,
        formattedMsg
      );

      // Update message cache
      setMessageCache((prev) => {
        const cached = prev[processedMessage.conversation];
        if (!cached) return prev;

        const exists = cached.messages.some(
          (m) => m._id === processedMessage._id
        );
        if (exists) return prev;

        return {
          ...prev,
          [processedMessage.conversation]: {
            ...cached,
            messages: [...cached.messages, formattedMsg],
          },
        };
      });

      // Mark as read via socket for instant feedback
      messagingSocketService.batchMarkRead(
        selectedConversationRef.current._id,
        [processedMessage._id]
      );
      setUnreadByConversation((prev) => ({
        ...prev,
        [selectedConversationRef.current._id]: 0,
      }));

      updateConversationLastMessage(processedMessage);
    } else {
      // Message is for a different conversation - update persistent cache
      const formattedMsg = formatMessage(processedMessage);
      messagingCacheService.addMessage(
        processedMessage.conversation,
        formattedMsg
      );
      messagingCacheService.updateConversationLastMessage(
        processedMessage.conversation,
        processedMessage
      );

      // Also update in-memory cache for consistency
      setMessageCache((prev) => {
        const cached = prev[processedMessage.conversation];
        if (!cached) return prev;

        const exists = cached.messages.some(
          (m) => m._id === processedMessage._id
        );
        if (exists) return prev;

        return {
          ...prev,
          [processedMessage.conversation]: {
            ...cached,
            messages: [...cached.messages, formattedMsg],
          },
        };
      });

      setUnreadByConversation((prev) => ({
        ...prev,
        [processedMessage.conversation]:
          (prev[processedMessage.conversation] || 0) + 1,
      }));
      setTotalUnread((prev) => prev + 1);
      updateConversationLastMessage(processedMessage);
    }
  };

  // NEW: Handle message sent confirmation from socket
  const handleMessageSent = (data) => {
    const { success, tempId, message, conversationId } = data;

    if (success && message) {
      // WhatsApp-style: Just update status, keep the message in place
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === tempId
            ? {
                ...msg,
                serverId: message._id,
                status: {
                  ...msg.status,
                  sending: false,
                  sent: true,
                },
                isTemp: false,
              }
            : msg
        )
      );

      // Update cache
      setMessageCache((prev) => {
        const convId = conversationId || message.conversation;
        const cached = prev[convId];
        if (!cached) return prev;

        return {
          ...prev,
          [convId]: {
            ...cached,
            messages: cached.messages.map((msg) =>
              msg._id === tempId
                ? {
                    ...msg,
                    serverId: message._id,
                    status: {
                      ...msg.status,
                      sending: false,
                      sent: true,
                    },
                    isTemp: false,
                  }
                : msg
            ),
          },
        };
      });

      // Update last message timestamp for sync
      if (message.createdAt) {
        messagingSocketService.setLastMessageTimestamp(message.createdAt);
      }
    }
  };

  // NEW: Handle message send error from socket
  const handleMessageError = (data) => {
    const { tempId, message: errorMessage, error: errorMsg } = data;
    const displayError = errorMessage || errorMsg || "Failed to send message";

    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === tempId
          ? {
              ...msg,
              status: {
                sending: false,
                sent: false,
                delivered: false,
                read: false,
                failed: true,
                error: displayError,
              },
            }
          : msg
      )
    );

    toast.error(displayError);
  };

  const handleMessageDelivered = (data) => {
    const { messageId, deliveredAt } = data;
    // Check both _id and serverId for matching (serverId is the real server ID)
    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === messageId || msg.serverId === messageId
          ? {
              ...msg,
              status: {
                ...msg.status,
                delivered: true,
                deliveredAt,
              },
            }
          : msg
      )
    );
  };

  // NEW: Handle batch messages delivered
  const handleMessagesDelivered = (data) => {
    const { messageIds, deliveredAt } = data;

    setMessages((prev) =>
      prev.map((msg) =>
        messageIds?.includes(msg._id) || messageIds?.includes(msg.serverId)
          ? {
              ...msg,
              status: {
                ...msg.status,
                delivered: true,
                deliveredAt,
              },
            }
          : msg
      )
    );
  };

  const handleMessagesRead = (data) => {
    const { conversationId, readAt, messageIds, messageId } = data;
    setMessages((prev) =>
      prev.map((msg) => {
        // If single messageId provided (from backend guide format)
        if (messageId && !messageIds) {
          // Mark all messages up to this messageId as read
          if (
            msg._id === messageId ||
            msg.serverId === messageId ||
            (msg.conversation === conversationId &&
              new Date(msg.createdAt) <= new Date(readAt))
          ) {
            return {
              ...msg,
              status: {
                ...msg.status,
                read: true,
                readAt,
              },
            };
          }
          return msg;
        }

        // If specific messageIds array provided, only mark those (check both _id and serverId)
        if (messageIds && messageIds.length > 0) {
          if (
            messageIds.includes(msg._id) ||
            messageIds.includes(msg.serverId)
          ) {
            return {
              ...msg,
              status: {
                ...msg.status,
                read: true,
                readAt,
              },
            };
          }
          return msg;
        }
        // Otherwise mark all messages in conversation before readAt
        if (
          msg.conversation === conversationId &&
          new Date(msg.createdAt) <= new Date(readAt)
        ) {
          return {
            ...msg,
            status: {
              ...msg.status,
              read: true,
              readAt,
            },
          };
        }
        return msg;
      })
    );
  };

  const handleMessageDeleted = (data) => {
    const { messageId, deletedBy } = data;
    const currentUserId = user?.id || user?._id;

    // Skip if we deleted this ourselves (already handled by optimistic update)
    if (deletedBy === currentUserId) {
      return;
    }

    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === messageId
          ? { ...msg, isDeleted: true, content: null, type: "text" }
          : msg
      )
    );
  };

  const handleMessageEdited = (data) => {
    const { messageId, content, newContent, editedAt, editedBy } = data;
    const currentUserId = user?.id || user?._id;

    // Skip if we edited this ourselves (already handled by optimistic update)
    if (editedBy === currentUserId) {
      return;
    }

    // Backend might send 'newContent' or 'content'
    const updatedContent = newContent || content;

    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === messageId
          ? { ...msg, content: updatedContent, isEdited: true, editedAt }
          : msg
      )
    );
  };

  const handleReactionAdded = (data) => {
    const { messageId, reaction, allReactions } = data;
    const currentUserId = user?.id || user?._id;

    // Skip if this is our own reaction (already handled by optimistic update)
    if (
      reaction.user?._id === currentUserId ||
      reaction.user === currentUserId
    ) {
      return;
    }

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg._id === messageId) {
          // If backend sends allReactions, use that (more reliable)
          if (allReactions) {
            return { ...msg, reactions: allReactions };
          }

          // Otherwise add the single reaction
          // Check if reaction already exists
          const exists = (msg.reactions || []).some(
            (r) =>
              (r.user?._id || r.user) ===
                (reaction.user?._id || reaction.user) &&
              r.emoji === reaction.emoji
          );
          if (exists) return msg;

          return { ...msg, reactions: [...(msg.reactions || []), reaction] };
        }
        return msg;
      })
    );
  };

  const handleReactionRemoved = (data) => {
    const { messageId, userId, emoji, allReactions } = data;
    const currentUserId = user?.id || user?._id;

    // Skip if this is our own reaction removal (already handled by optimistic update)
    if (userId === currentUserId) {
      return;
    }

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg._id === messageId) {
          // If backend sends allReactions, use that (more reliable)
          if (allReactions) {
            return { ...msg, reactions: allReactions };
          }

          return {
            ...msg,
            reactions: (msg.reactions || []).filter(
              (r) => !((r.user?._id || r.user) === userId && r.emoji === emoji)
            ),
          };
        }
        return msg;
      })
    );
  };

  const handleUserTyping = (data) => {
    const { conversationId, user: typingUser } = data;
    if (
      selectedConversationRef.current &&
      conversationId === selectedConversationRef.current._id
    ) {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.set(typingUser._id, typingUser);
        setTimeout(() => {
          setTypingUsers((current) => {
            const updated = new Map(current);
            updated.delete(typingUser._id);
            return updated;
          });
        }, 5000);
        return newMap;
      });
    }
  };

  const handleUserStoppedTyping = (data) => {
    const { conversationId, userId } = data;
    if (
      selectedConversationRef.current &&
      conversationId === selectedConversationRef.current._id
    ) {
      setTypingUsers((prev) => {
        const newMap = new Map(prev);
        newMap.delete(userId);
        return newMap;
      });
    }
  };

  const handleUserOnline = (data) => {
    const { userId } = data;
    setOnlineUsers((prev) => new Set([...prev, userId]));
    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.otherParticipant?._id === userId) {
          return {
            ...conv,
            otherParticipant: { ...conv.otherParticipant, isOnline: true },
          };
        }
        return conv;
      })
    );
  };

  const handleUserOffline = (data) => {
    const { userId, lastSeen } = data;
    setOnlineUsers((prev) => {
      const newSet = new Set(prev);
      newSet.delete(userId);
      return newSet;
    });
    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.otherParticipant?._id === userId) {
          return {
            ...conv,
            otherParticipant: {
              ...conv.otherParticipant,
              isOnline: false,
              lastSeen: lastSeen || new Date().toISOString(),
            },
          };
        }
        return conv;
      })
    );
  };

  const handleUserPresenceUpdate = (data) => {
    const { userId, isOnline, status, lastSeen } = data;
    if (isOnline) {
      setOnlineUsers((prev) => new Set([...prev, userId]));
    } else {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.otherParticipant?._id === userId) {
          return {
            ...conv,
            otherParticipant: {
              ...conv.otherParticipant,
              isOnline,
              status: status || (isOnline ? "online" : "offline"),
              lastSeen: lastSeen || conv.otherParticipant.lastSeen,
            },
          };
        }
        return conv;
      })
    );
  };

  // NEW: Handle online contacts list on connect
  const handleOnlineContactsList = (data) => {
    const { contacts } = data;

    if (contacts && Array.isArray(contacts)) {
      const onlineUserIds = new Set(
        contacts.map((c) => c.userId || c._id || c)
      );
      setOnlineUsers(onlineUserIds);

      // Update conversations with online status
      setConversations((prev) =>
        prev.map((conv) => {
          const isOnline = onlineUserIds.has(conv.otherParticipant?._id);
          return {
            ...conv,
            otherParticipant: {
              ...conv.otherParticipant,
              isOnline,
              status: isOnline
                ? "online"
                : conv.otherParticipant?.status || "offline",
            },
          };
        })
      );
    }
  };

  // NEW: Handle connection established
  const handleConnectionEstablished = (data) => {
    // Connection is ready, features available
  };

  // NEW: Handle sync complete after reconnection
  const handleSyncComplete = (data) => {
    const { messages: syncedMessages, unreadCounts, onlineContacts } = data;

    // Merge synced messages
    if (syncedMessages && syncedMessages.length > 0) {
      syncedMessages.forEach((msg) => {
        if (
          selectedConversationRef.current &&
          msg.conversation === selectedConversationRef.current._id
        ) {
          setMessages((prev) => {
            const exists = prev.some(
              (m) => m._id === msg._id || m.serverId === msg._id
            );
            if (exists) {
              return prev;
            }
            return [...prev, formatMessage(msg)].sort(
              (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
            );
          });
        }
      });
    }

    // Update unread counts
    if (unreadCounts) {
      setUnreadByConversation(unreadCounts);
      const total = Object.values(unreadCounts).reduce((a, b) => a + b, 0);
      setTotalUnread(total);
    }

    // Update online contacts
    if (onlineContacts && Array.isArray(onlineContacts)) {
      setOnlineUsers(new Set(onlineContacts));
    }
  };

  // ============================================
  // ENCRYPTION
  // ============================================

  const initializeEncryption = async () => {
    try {
      setIsE2EEEnabled(false);
      setEncryptionReady(true);
    } catch (error) {
setIsE2EEEnabled(false);
      setEncryptionReady(true);
    }
  };

  // ============================================
  // DATA FETCHING
  // ============================================

  // NEW: Fetch online contacts
  const fetchOnlineContacts = async () => {
    try {
      const response = await messagingService.getOnlineContacts();
      if (response.success && response.data) {
        const { online } = response.data;
        if (online && Array.isArray(online)) {
          setOnlineUsers(new Set(online.map((c) => c._id)));
        }
      }
    } catch (err) { /* ignored */ }
  };

  // NEW: Fetch conversations with presence (preferred over regular getConversations)
  const fetchConversationsWithPresence = async () => {
    // Only show loading if we don't have cached data
    const cachedData = messagingCacheService.getConversations();
    if (!cachedData) {
      setLoading(true);
    }
    setError(null);

    try {
      // Try new endpoint first
      let response;
      try {
        response = await messagingService.getConversationsWithPresence({
          page: 1,
          limit: 50,
        });
      } catch (presenceError) {
        // Fall back to regular endpoint if new one not available
response = await messagingService.getConversations({
          page: 1,
          limit: 50,
          includeArchived: false,
        });
      }

      if (response.success) {
        const conversations = response.data.conversations || [];
        const uniqueConversations = conversations.reduce((acc, conv) => {
          if (!acc.find((c) => c._id === conv._id)) {
            acc.push(conv);
          }
          return acc;
        }, []);
        setConversations(uniqueConversations);

        // Save to persistent cache for instant load on next visit
        messagingCacheService.setConversations({
          conversations: uniqueConversations,
          fetchedAt: Date.now(),
        });

        // Extract online users from presence data
        const onlineUserIds = uniqueConversations
          .filter((c) => c.otherParticipant?.isOnline)
          .map((c) => c.otherParticipant._id);
        setOnlineUsers((prev) => new Set([...prev, ...onlineUserIds]));
      }
    } catch (err) {
// Only show error if we don't have cached data to show
      if (!cachedData) {
        setError("Failed to load conversations.");
        toast.error("Failed to load conversations");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await messagingService.getConversations({
        page: 1,
        limit: 50,
        includeArchived: false,
      });
      if (response.success) {
        const conversations = response.data.conversations || [];
        const uniqueConversations = conversations.reduce((acc, conv) => {
          if (!acc.find((c) => c._id === conv._id)) {
            acc.push(conv);
          }
          return acc;
        }, []);
        setConversations(uniqueConversations);
      }
    } catch (err) {
setError("Failed to load conversations.");
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch messages with cursor-based pagination
   * ⚠️ CRITICAL: Backend returns messages in CHRONOLOGICAL order (oldest first)
   * ⚠️ Backend uses `hasNext` NOT `hasMore`!
   * When loading more (older) messages, PREPEND them to the beginning
   */
  const fetchMessages = async (
    conversationId,
    cursor = null,
    forceRefresh = false
  ) => {
    // Check persistent cache first (for initial load, not pagination)
    if (!cursor && !forceRefresh) {
      // Try persistent cache first
      const persistentCached =
        messagingCacheService.getMessages(conversationId);
      if (persistentCached && persistentCached.messages) {
setMessages(persistentCached.messages);

        // ⚠️ Infer hasNext from cached data
        const cachedHasNext =
          persistentCached.hasNext ??
          persistentCached.hasMore ??
          (persistentCached.messages.length >= 50 &&
            !!(persistentCached.nextCursor || persistentCached.cursor));

        setPagination((prev) => ({
          ...prev,
          hasNext: cachedHasNext,
          nextCursor:
            persistentCached.nextCursor || persistentCached.cursor || null,
          isLoading: false,
          isLoadingMore: false,
        }));

        // Also sync to in-memory cache
        setMessageCache((prev) => ({
          ...prev,
          [conversationId]: persistentCached,
        }));

        // Still mark as read
        const unreadIds = persistentCached.messages
          .filter((m) => m.senderId !== "me")
          .map((m) => m._id);
        if (unreadIds.length > 0) {
          messagingSocketService.batchMarkRead(conversationId, unreadIds);
        }
        // Clear unread count and decrement totalUnread
        setUnreadByConversation((prev) => {
          const previousCount = prev[conversationId] || 0;
          if (previousCount > 0) {
            setTotalUnread((total) => Math.max(0, total - previousCount));
          }
          return { ...prev, [conversationId]: 0 };
        });

        // Background refresh to get any new messages
        fetchMessagesBackground(conversationId);
        return;
      }

      // Fallback to in-memory cache
      if (messageCache[conversationId]) {
        const cached = messageCache[conversationId];
        setMessages(cached.messages);

        // ⚠️ Infer hasNext from cached data
        const cachedHasNext =
          cached.hasNext ??
          cached.hasMore ??
          (cached.messages.length >= 50 &&
            !!(cached.nextCursor || cached.cursor));

        setPagination((prev) => ({
          ...prev,
          hasNext: cachedHasNext,
          nextCursor: cached.nextCursor || cached.cursor || null,
          isLoading: false,
          isLoadingMore: false,
        }));

        // Still mark as read
        messagingSocketService.batchMarkRead(
          conversationId,
          cached.messages.filter((m) => m.senderId !== "me").map((m) => m._id)
        );
        // Clear unread count and decrement totalUnread
        setUnreadByConversation((prev) => {
          const previousCount = prev[conversationId] || 0;
          if (previousCount > 0) {
            setTotalUnread((total) => Math.max(0, total - previousCount));
          }
          return { ...prev, [conversationId]: 0 };
        });
        return;
      }
    }

    // Set loading state - use new pagination state
    if (cursor) {
      setPagination((prev) => ({ ...prev, isLoadingMore: true }));
    } else {
      setLoadingMessages(true);
      setPagination((prev) => ({ ...prev, isLoading: true }));
    }
    setError(null);

    try {
      const response = await messagingService.getMessages(conversationId, {
        cursor,
        limit: 50,
      });

      if (response.success) {
        const fetchedMessages = response.data.messages || [];
        const processedMessages = await Promise.all(
          fetchedMessages.map(async (msg) => {
            try {
              const decryptedMsg =
                await encryptionManager.processReceivedMessage(
                  msg,
                  conversationId
                );
              return formatMessage(decryptedMsg);
            } catch (error) {
              return formatMessage({
                ...msg,
                content: "⚠️ [Failed to decrypt]",
                error: true,
              });
            }
          })
        );

        // 🩹 Track processed message IDs for duplicate detection
        const processedMessageIds = new Set(
          processedMessages.map((m) => m._id)
        );

        // Check current messages to see if all returned messages are duplicates
        // (This helps detect backend cursor bug before state updates)
        let allMessagesDuplicated = false;

        if (cursor) {
          // ⚠️ CRITICAL: Loading older messages - PREPEND to BEGINNING
          // Messages come in chronological order (oldest first) from backend
          // ⚠️ DEDUPLICATE: Filter out any messages that already exist

          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m._id));
            const newMessages = processedMessages.filter(
              (m) => !existingIds.has(m._id)
            );

            // Track if ALL were duplicates
            allMessagesDuplicated =
              newMessages.length === 0 && processedMessages.length > 0;

            return [...newMessages, ...prev];
          });

          // Update cache with additional messages (also deduplicated)
          setMessageCache((prev) => {
            const existingCache = prev[conversationId]?.messages || [];
            const existingIds = new Set(existingCache.map((m) => m._id));
            const newMessages = processedMessages.filter(
              (m) => !existingIds.has(m._id)
            );
            return {
              ...prev,
              [conversationId]: {
                messages: [...newMessages, ...existingCache],
                // ⚠️ Use hasNext from backend (NOT hasMore)
                nextCursor: response.data.pagination.nextCursor,
                prevCursor: response.data.pagination.prevCursor,
                hasNext: response.data.pagination.hasNext,
                hasPrev: response.data.pagination.hasPrev,
              },
            };
          });
        } else {
          // Initial load - CRITICAL FIX: Preserve any pending/temp messages when updating cache
          // This prevents race condition where API response overwrites temp messages
          setMessageCache((prev) => {
            const existingCache = prev[conversationId];

            // Find any pending messages (temp messages or messages sent after the last fetched message)
            const pendingMessages =
              existingCache?.messages?.filter((msg) => {
                // Keep temp messages
                if (msg.isTemp || msg._id?.startsWith("temp_")) {
                  return true;
                }
                // Keep messages that aren't in the fetched data (newly sent)
                const existsInFetched = processedMessages.some(
                  (pm) => pm._id === msg._id
                );
                if (!existsInFetched && msg.senderId === "me") {
                  // Check if this message was sent after the last fetched message
                  const lastFetchedTime =
                    processedMessages.length > 0
                      ? new Date(
                          processedMessages[
                            processedMessages.length - 1
                          ].createdAt
                        ).getTime()
                      : 0;
                  const msgTime = new Date(msg.createdAt).getTime();
                  if (msgTime > lastFetchedTime) {
                    return true;
                  }
                }
                return false;
              }) || [];

            // Merge: fetched messages + pending messages (sorted by time)
            const mergedMessages = [
              ...processedMessages,
              ...pendingMessages,
            ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

            return {
              ...prev,
              [conversationId]: {
                messages: mergedMessages,
                // ⚠️ Use correct backend field names
                nextCursor: response.data.pagination.nextCursor,
                prevCursor: response.data.pagination.prevCursor,
                hasNext: response.data.pagination.hasNext,
                hasPrev: response.data.pagination.hasPrev,
              },
            };
          });

          // Also update messages state with merged data
          setMessages((prev) => {
            // Find any pending messages from current state
            const pendingMessages = prev.filter((msg) => {
              if (msg.isTemp || msg._id?.startsWith("temp_")) return true;
              const existsInFetched = processedMessages.some(
                (pm) => pm._id === msg._id
              );
              if (!existsInFetched && msg.senderId === "me") {
                const lastFetchedTime =
                  processedMessages.length > 0
                    ? new Date(
                        processedMessages[
                          processedMessages.length - 1
                        ].createdAt
                      ).getTime()
                    : 0;
                const msgTime = new Date(msg.createdAt).getTime();
                if (msgTime > lastFetchedTime) return true;
              }
              return false;
            });

            const mergedMessages = [
              ...processedMessages,
              ...pendingMessages,
            ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

            // Save to persistent cache (excluding temp messages)
            const persistableMessages = mergedMessages.filter(
              (m) => !m.isTemp && !m._id?.startsWith("temp_")
            );
            messagingCacheService.setMessages(conversationId, {
              messages: persistableMessages,
              // ⚠️ Use correct backend field names
              nextCursor: response.data.pagination.nextCursor,
              prevCursor: response.data.pagination.prevCursor,
              hasNext: response.data.pagination.hasNext,
              hasPrev: response.data.pagination.hasPrev,
            });

            return mergedMessages;
          });
        }

        // ⚠️ Update pagination state with backend response
        // Backend might NOT return hasNext/hasPrev - infer from data
        const paginationData = response.data.pagination || {};

        // 🚨 CRITICAL BUG DETECTION: Check if cursor didn't change (backend issue)
        let newNextCursor =
          paginationData.nextCursor ?? paginationData.cursor ?? null;

        if (cursor && newNextCursor === cursor) {
          // 🩹 FRONTEND WORKAROUND: Calculate correct cursor from oldest message in response
          // This fixes the issue until backend is properly fixed
          if (processedMessages.length > 0 && !allMessagesDuplicated) {
            // Find the oldest message in this batch that we don't already have
            // First, get current message IDs from state
            const currentMessageIds = new Set();
            setMessages((prev) => {
              prev.forEach((m) => currentMessageIds.add(m._id));
              return prev; // Don't modify state, just read
            });

            // Find unique messages we received
            const uniqueNewMessages = processedMessages.filter(
              (m) =>
                !processedMessageIds.has(m._id) || !currentMessageIds.has(m._id)
            );

            // Find the oldest message in this batch (smallest createdAt)
            const messagesToCheck =
              uniqueNewMessages.length > 0
                ? uniqueNewMessages
                : processedMessages;
            const oldestMessage = messagesToCheck.reduce((oldest, msg) => {
              const msgDate = new Date(msg.createdAt).getTime();
              const oldestDate = new Date(oldest.createdAt).getTime();
              return msgDate < oldestDate ? msg : oldest;
            }, messagesToCheck[0]);

            const calculatedCursor = oldestMessage.createdAt;
            // Only use calculated cursor if it's actually different (older)
            if (
              new Date(calculatedCursor).getTime() < new Date(cursor).getTime()
            ) {
              newNextCursor = calculatedCursor;
            } else {
              // If no older messages, we've reached the end
              newNextCursor = null;
            }
          } else if (allMessagesDuplicated) {
            // If all messages were duplicates, stop pagination - we've loaded everything
            newNextCursor = null;
          }
        }

        // Recalculate hasNext based on corrected cursor and duplicate detection
        // If all messages were duplicates, there's nothing more to load (we've seen them all)
        const correctedHasNext =
          !allMessagesDuplicated &&
          newNextCursor !== null &&
          newNextCursor !== cursor &&
          processedMessages.length > 0;

        setPagination({
          hasNext: correctedHasNext,
          hasPrev: paginationData.hasPrev ?? false,
          nextCursor: newNextCursor,
          prevCursor: paginationData.prevCursor ?? null,
          isLoading: false,
          isLoadingMore: false,
        });

        // Mark as read via socket with deduplication
        const unreadMessageIds = processedMessages
          .filter((m) => m.senderId !== "me")
          .map((m) => m._id);
        if (unreadMessageIds.length > 0) {
          messagingSocketService.batchMarkRead(
            conversationId,
            unreadMessageIds
          );
        }
        // Clear unread count and decrement totalUnread
        setUnreadByConversation((prev) => {
          const previousCount = prev[conversationId] || 0;
          if (previousCount > 0) {
            setTotalUnread((total) => Math.max(0, total - previousCount));
          }
          return { ...prev, [conversationId]: 0 };
        });
      }
    } catch (err) {
setError("Failed to load messages.");
      toast.error("Failed to load messages");
      setPagination((prev) => ({
        ...prev,
        isLoading: false,
        isLoadingMore: false,
      }));
    } finally {
      setLoadingMessages(false);
    }
  };

  // Background fetch for stale-while-revalidate pattern
  const fetchMessagesBackground = async (conversationId) => {
    try {
      const response = await messagingService.getMessages(conversationId, {
        cursor: null,
        limit: 50,
      });

      if (response.success) {
        const fetchedMessages = response.data.messages || [];
        const processedMessages = await Promise.all(
          fetchedMessages.map(async (msg) => {
            try {
              const decryptedMsg =
                await encryptionManager.processReceivedMessage(
                  msg,
                  conversationId
                );
              return formatMessage(decryptedMsg);
            } catch (error) {
              return formatMessage({
                ...msg,
                content: "⚠️ [Failed to decrypt]",
                error: true,
              });
            }
          })
        );

        // Only update if still on the same conversation
        if (selectedConversationRef.current?._id === conversationId) {
          setMessages((prev) => {
            // Merge with any temp messages
            const pendingMessages = prev.filter(
              (msg) => msg.isTemp || msg._id?.startsWith("temp_")
            );
            const merged = [...processedMessages, ...pendingMessages].sort(
              (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
            );
            return merged;
          });

          // ⚠️ CRITICAL: Also update pagination state!
          const paginationData = response.data.pagination || {};
          const hasNextValue =
            paginationData.hasNext !== undefined
              ? paginationData.hasNext
              : paginationData.hasMore !== undefined
              ? paginationData.hasMore
              : processedMessages.length >= 50 && !!paginationData.nextCursor;

          setPagination((prev) => ({
            ...prev,
            hasNext: hasNextValue,
            nextCursor: paginationData.nextCursor || null,
            prevCursor: paginationData.prevCursor || null,
            isLoading: false,
            isLoadingMore: false,
          }));
}

        // Update caches
        const hasNextForCache =
          response.data.pagination?.hasNext ??
          response.data.pagination?.hasMore ??
          (processedMessages.length >= 50 &&
            !!response.data.pagination?.nextCursor);

        const cacheData = {
          messages: processedMessages,
          nextCursor: response.data.pagination?.nextCursor,
          prevCursor: response.data.pagination?.prevCursor,
          hasNext: hasNextForCache,
          hasPrev: response.data.pagination?.hasPrev ?? false,
        };

        setMessageCache((prev) => ({
          ...prev,
          [conversationId]: cacheData,
        }));

        messagingCacheService.setMessages(conversationId, cacheData);
      }
    } catch (err) {
// Silent fail - we already have cached data showing
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await messagingService.getUnreadCount();
      if (response.success) {
        setTotalUnread(response.data.totalUnread || 0);
        setUnreadByConversation(response.data.byConversation || {});

        // Save to persistent cache
        messagingCacheService.setUnreadCount({
          totalUnread: response.data.totalUnread || 0,
          byConversation: response.data.byConversation || {},
        });
      }
    } catch (err) { /* ignored */ }
  };

  // ============================================
  // CONVERSATION SELECTION
  // ============================================

  const updateConversationLastMessage = useCallback(
    (message) => {
      // Update persistent cache
      messagingCacheService.updateConversationLastMessage(
        message.conversation,
        {
          content: message.content || "Message",
          createdAt: message.createdAt || new Date().toISOString(),
          sender: message.sender || { _id: user?.id || user?._id },
        }
      );

      setConversations((prev) => {
        const updated = prev
          .map((conv) => {
            if (conv._id === message.conversation) {
              return {
                ...conv,
                lastMessage: {
                  content: message.content || "Message",
                  createdAt: message.createdAt || new Date().toISOString(),
                  sender: message.sender || { _id: user?.id || user?._id },
                },
                lastActivity: message.createdAt || new Date().toISOString(),
              };
            }
            return conv;
          })
          .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));

        return updated.reduce((acc, conv) => {
          if (!acc.find((c) => c._id === conv._id)) {
            acc.push(conv);
          }
          return acc;
        }, []);
      });
    },
    [user]
  );

  // ============================================
  // CONVERSATION SELECTION
  // ============================================

  const handleConversationSelect = (conversation) => {
    // If same conversation is already selected, don't re-fetch or clear messages
    if (selectedConversation && selectedConversation._id === conversation._id) {
      return;
    }

    // Leave previous conversation room (CRITICAL for backend)
    if (selectedConversation) {
      messagingSocketService.leaveConversation(selectedConversation._id);
    }

    // Stop typing if we were typing in previous conversation
    if (selectedConversation && isTypingRef.current) {
      messagingSocketService.sendTypingStop(selectedConversation._id);
      isTypingRef.current = false;
    }

    // Clear typing timer
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    // Clear marked as read tracking for new conversation
    messagingSocketService.clearMarkedAsRead();

    setSelectedConversation(conversation);
    setShowEmojiPicker(false);
    setShowNewMessageModal(false);

    // Join new conversation room (CRITICAL for real-time updates!)
    messagingSocketService.joinConversation(conversation._id);

    // CRITICAL: Clear messages and reset pagination state
    setMessages([]);
    // ⚠️ Reset pagination state with proper backend field names
    setPagination({
      hasNext: true, // ⚠️ Backend uses hasNext NOT hasMore
      hasPrev: false,
      nextCursor: null,
      prevCursor: null,
      isLoading: false,
      isLoadingMore: false,
    });
    setTypingUsers(new Map());
    fetchMessages(conversation._id);
    
    // NEW: Clear messaging unread count for this conversation
    if (clearMessagingUnread && conversation.unreadCount) {
      clearMessagingUnread(conversation.unreadCount);
    }
  };

  const handleNewMessageClick = () => {
    setShowNewMessageModal(true);
  };

  const handleNewMessageModalClose = () => {
    setShowNewMessageModal(false);
  };

  const handleSelectContactFromModal = (conversation) => {
    setConversations((prev) => {
      const exists = prev.find((c) => c._id === conversation._id);
      if (!exists) {
        return [conversation, ...prev];
      }
      return prev;
    });
    handleConversationSelect(conversation);
  };

  // ============================================
  // MESSAGE SENDING
  // ============================================

  const handleSendMessage = async (e) => {
    e && e.preventDefault();
    if (!newMessage.trim()) return;
    if (!selectedConversation) return;

    const content = newMessage.trim();
    const messageType = "text";
    const replyToMessage = replyingTo;
    const tempId = `temp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const currentUserId = user?.id || user?._id;

    // CRITICAL: Keep input focused BEFORE clearing state (prevents keyboard close)
    messageInputRef.current?.focus();

    // Clear input immediately for responsiveness
    setNewMessage("");
    setReplyingTo(null);

    // OPTIMISTIC UPDATE: Add temp message immediately for instant feedback
    // WhatsApp-style: Keep tempId for UI, store serverId when confirmed
    const tempMessage = {
      _id: tempId,
      serverId: null, // Will be populated when server confirms
      content,
      type: messageType, // CRITICAL: MessageBubble checks 'type' not 'messageType'
      messageType,
      senderId: "me", // Use "me" to match rendering logic for isMine check
      sender: { _id: currentUserId, name: user?.name },
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      status: {
        sending: true,
        sent: false,
        delivered: false,
        read: false,
        failed: false,
      },
      isTemp: true,
      conversation: selectedConversation._id,
      replyTo: replyToMessage
        ? {
            _id: replyToMessage._id,
            content: replyToMessage.content,
            sender: replyToMessage.sender,
          }
        : null,
    };

    // Add temp message to UI immediately
    setMessages((prev) => [...prev, tempMessage]);

    // CRITICAL FIX: Also add temp message to cache immediately
    // This ensures the message persists if user clicks away and back
    setMessageCache((prev) => {
      const cached = prev[selectedConversation._id];
      if (!cached) {
        // Create new cache entry with just this message
        return {
          ...prev,
          [selectedConversation._id]: {
            messages: [tempMessage],
            cursor: null,
            hasMore: false,
          },
        };
      }
      return {
        ...prev,
        [selectedConversation._id]: {
          ...cached,
          messages: [...cached.messages, tempMessage],
        },
      };
    });

    try {
      let messagePayload = { content, messageType };
      if (replyToMessage) {
        messagePayload.replyTo = replyToMessage._id;
      }

      const response = await messagingService.sendMessage(
        selectedConversation._id,
        messagePayload
      );

      if (response.success) {
        const realMessage = response.data.message;

        // ✅ REPLACE TEMP ID WITH REAL MONGODB ID
        // This ensures edit/delete operations work correctly
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === tempId
              ? {
                  ...realMessage, // Use complete message from backend
                  _id: realMessage._id, // Replace temp ID with real MongoDB ID
                  content: msg.content, // Keep optimistic content
                  type: msg.type,
                  messageType: msg.messageType,
                  sender: msg.sender,
                  senderId: msg.senderId,
                  timestamp: msg.timestamp,
                  createdAt: realMessage.createdAt || msg.createdAt,
                  replyTo: msg.replyTo,
                  status: {
                    sending: false,
                    sent: true,
                    delivered: false,
                    read: false,
                    failed: false,
                  },
                  isTemp: false,
                }
              : msg
          )
        );

        // Update cache with real MongoDB ID
        setMessageCache((prev) => {
          const cached = prev[selectedConversation._id];
          if (!cached) return prev;

          return {
            ...prev,
            [selectedConversation._id]: {
              ...cached,
              messages: cached.messages.map((msg) =>
                msg._id === tempId
                  ? {
                      ...realMessage,
                      _id: realMessage._id, // Replace temp ID with real ID
                      content: msg.content,
                      type: msg.type,
                      messageType: msg.messageType,
                      sender: msg.sender,
                      senderId: msg.senderId,
                      timestamp: msg.timestamp,
                      createdAt: realMessage.createdAt || msg.createdAt,
                      replyTo: msg.replyTo,
                      status: {
                        sending: false,
                        sent: true,
                        delivered: false,
                        read: false,
                        failed: false,
                      },
                      isTemp: false,
                    }
                  : msg
              ),
            },
          };
        });

        // Update persistent cache with the confirmed message
        const confirmedMessage = {
          ...tempMessage,
          _id: realMessage._id,
          serverId: realMessage._id,
          status: {
            sending: false,
            sent: true,
            delivered: false,
            read: false,
            failed: false,
          },
          isTemp: false,
        };
        messagingCacheService.updateMessage(
          selectedConversation._id,
          tempId,
          confirmedMessage
        );

        // Also update the conversation's last message in persistent cache
        messagingCacheService.updateConversationLastMessage(
          selectedConversation._id,
          {
            content: realMessage.content || content,
            createdAt: realMessage.createdAt || new Date().toISOString(),
            sender: { _id: currentUserId, name: user?.name },
          }
        );

        const messageForUpdate = {
          ...realMessage,
          content: realMessage.content || content,
          conversation: selectedConversation._id,
          sender: { _id: currentUserId, name: user?.name },
        };
        updateConversationLastMessage(messageForUpdate);
      } else {
        // Mark temp message as failed
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === tempId
              ? {
                  ...msg,
                  status: {
                    sending: false,
                    sent: false,
                    delivered: false,
                    read: false,
                    failed: true,
                  },
                }
              : msg
          )
        );
      }
    } catch (error) {
// Mark temp message as failed on error
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === tempId
            ? {
                ...msg,
                status: {
                  sending: false,
                  sent: false,
                  delivered: false,
                  read: false,
                  failed: true,
                  error: error.message,
                },
              }
            : msg
        )
      );
      toast.error(error.message || "Failed to send message");
    }
  };

  // ============================================
  // TYPING INDICATORS (Debounced)
  // ============================================

  const handleTypingStop = useCallback(() => {
    if (!selectedConversation) return;

    // Only send stop if we were typing
    if (isTypingRef.current) {
      isTypingRef.current = false;
      messagingSocketService.sendTypingStop(selectedConversation._id);
    }

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  }, [selectedConversation]);

  const handleTypingStart = useCallback(() => {
    if (!selectedConversation) return;

    // Only send typing_start ONCE until we stop
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      messagingSocketService.sendTypingStart(selectedConversation._id);
    }

    // Reset the auto-stop timer on each keystroke
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    // Auto-stop after 3 seconds of no typing
    typingTimerRef.current = setTimeout(() => {
      handleTypingStop();
    }, 3000);
  }, [selectedConversation, handleTypingStop]);

  const handleInputChange = useCallback((value) => {
    setNewMessage(value);

    // Only trigger typing if there's actual content
    if (value.trim()) {
      handleTypingStart();
    } else {
      handleTypingStop();
    }
  }, [handleTypingStart, handleTypingStop]);

  // ============================================
  // INFINITE SCROLL (Per Backend Guide)
  // ============================================

  /**
   * Load more (older) messages using cursor-based pagination
   */
  const loadMoreMessages = useCallback(() => {
    if (pagination.isLoadingMore) {
      return;
    }
    if (!pagination.hasNext) {
      return;
    }
    if (!pagination.nextCursor) {
      return;
    }
    if (!selectedConversation) {
      return;
    }

    // ⚠️ Mark that we're loading older messages - used to prevent auto-scroll to bottom
    justLoadedOlderMessagesRef.current = true;

    fetchMessages(selectedConversation._id, pagination.nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.isLoadingMore,
    pagination.hasNext,
    pagination.nextCursor,
    selectedConversation,
  ]);

  // ============================================
  // JUMP TO MESSAGE (Search & Navigation)
  // ============================================

  /**
   * Handle jumping to a specific message from search or quote
   * Loads message context and highlights the target
   */
  const handleJumpToMessage = useCallback(
    async (conversationId, messageId) => {
// If different conversation, switch to it first
      if (selectedConversation?._id !== conversationId) {
        const targetConversation = conversations.find(
          (c) => c._id === conversationId
        );
        if (targetConversation) {
          handleConversationSelect(targetConversation);
        } else {
          // Fetch conversation details
          try {
            const response = await messagingService.getConversationDetails(
              conversationId
            );
            if (response.success) {
              handleConversationSelect(response.data.conversation);
            }
          } catch (error) {
            toast.error("Could not load conversation");
            return;
          }
        }
      }

      // Jump to message and get the target index
      const index = await jumpToMessage(messageId);
      if (index !== null) {
// Highlight the message
        setHighlightedMessageId(messageId);
// Scroll to the message with retry mechanism to ensure DOM is ready
        const scrollToMessage = (retries = 10) => {
          // Debug: Check all message elements in DOM
          const allMessageElements = document.querySelectorAll('[data-message-id]');
if (allMessageElements.length > 0) {
}
          
          const messageElement = document.querySelector(
            `[data-message-id="${messageId}"]`
          );
          
          if (messageElement) {
messageElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            
            // Clear highlight after 3 seconds
            setTimeout(() => {
setHighlightedMessageId(null);
              clearHighlight();
            }, 3000);
          } else if (retries > 0) {
setTimeout(() => scrollToMessage(retries - 1), 300);
          } else {
}
        };

        // Start scrolling after DOM updates
        setTimeout(() => scrollToMessage(), 500);
      } else {
}
    },
    [
      selectedConversation,
      conversations,
      jumpToMessage,
      clearHighlight,
    ]
  );

  /**
   * Exit jumped mode and return to normal latest messages view
   */
  const handleExitJumpedMode = useCallback(() => {
    exitJumpedMode();
    if (selectedConversation) {
      fetchMessages(selectedConversation._id, null, true);
    }
  }, [exitJumpedMode, selectedConversation]);

  /**
   * ⚠️ CRITICAL: Bidirectional scroll handler
   * - Scroll UP = Load older messages
   * - Scroll DOWN = Load newer messages (in jumped mode only)
   */
  const handleScroll = useCallback(
    (e) => {
      const container = e.target;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      // In jumped mode: bidirectional scrolling
      if (isJumpedMode) {
        // Near top - load older messages
        if (scrollTop < 100 && jumpHasOlder) {
          prevScrollHeightRef.current = scrollHeight;
          loadOlderJumped();
        }

        // Near bottom - load newer messages
        if (scrollTop + clientHeight > scrollHeight - 100 && jumpHasNewer) {
          loadNewerJumped();
        }
      }
      // Normal mode: only upward scroll (older messages)
      else {
        if (
          scrollTop < 100 &&
          pagination.hasNext &&
          !pagination.isLoadingMore &&
          pagination.nextCursor
        ) {
          prevScrollHeightRef.current = scrollHeight;
          loadMoreMessages();
        }
      }
    },
    [
      isJumpedMode,
      jumpHasOlder,
      jumpHasNewer,
      loadOlderJumped,
      loadNewerJumped,
      pagination.hasNext,
      pagination.isLoadingMore,
      pagination.nextCursor,
      loadMoreMessages,
    ]
  );

  /**
   * Maintain scroll position after loading older messages
   * This prevents the "jump" when prepending messages
   */
  useEffect(() => {
    if (!pagination.isLoadingMore && prevScrollHeightRef.current > 0) {
      const container = messagesContainerRef.current;
      if (container) {
        // Calculate new scroll position to maintain view
        const newScrollHeight = container.scrollHeight;
        const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
        container.scrollTop = scrollDiff;
      }
      prevScrollHeightRef.current = 0;
      // Reset the flag after a short delay to allow scroll position to settle
      setTimeout(() => {
        justLoadedOlderMessagesRef.current = false;
      }, 100);
    }
  }, [messages.length, pagination.isLoadingMore]);

  // ============================================
  // UTILITY
  // ============================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to bottom on initial load and new messages (NOT when loading older messages)
  useEffect(() => {
    // ⚠️ CRITICAL: Don't scroll to bottom if we just loaded older messages!
    if (justLoadedOlderMessagesRef.current) {
return;
    }
    if (!pagination.isLoadingMore && !pagination.isLoading) {
      // Only auto-scroll on initial load or new messages
      scrollToBottom();
    }
  }, [messages.length, pagination.isLoadingMore, pagination.isLoading]);

  // Auto-scroll when typing indicator appears
  useEffect(() => {
    if (typingUsers.size > 0) {
      scrollToBottom();
    }
  }, [typingUsers.size]);

  const handleEmojiClick = (emojiData) => {
    // Update input value directly for instant feedback
    if (messageInputRef.current) {
      const currentValue = messageInputRef.current.value || "";
      const newValue = currentValue + emojiData.emoji;
      messageInputRef.current.value = newValue;
      setNewMessage(newValue);
      messageInputRef.current.focus();
    }
  };

  const handleReplyToMessage = (message) => {
    setReplyingTo(message);
    messageInputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleShowMenuViaGesture = (messageId) => {
    setLongPressMenuMessageId(messageId);
  };

  // ============================================
  // MESSAGE ACTIONS
  // ============================================

  const handleEditMessage = (message) => {
    // Prevent editing temp messages that are still being sent
    if (message.isTemp || message._id?.startsWith('temp_') || message.status?.sending) {
      toast.error("Please wait for the message to be delivered before editing");
      return;
    }
    setEditingMessageId(message._id);
    setEditContent(message.content);
    messageInputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent("");
  };

  const handleSubmitEdit = async (messageId) => {
    if (!editContent.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    if (
      editContent.trim() === messages.find((m) => m._id === messageId)?.content
    ) {
      handleCancelEdit();
      return;
    }

    try {
      const response = await messagingService.editMessage(
        messageId,
        editContent.trim()
      );

      if (response.success) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? {
                  ...msg,
                  content: editContent.trim(),
                  isEdited: true,
                  editedAt: new Date().toISOString(),
                }
              : msg
          )
        );
        handleCancelEdit();
        toast.success("Message edited successfully");
      }
    } catch (error) {
toast.error(error.message || "Failed to edit message");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    // Find the message to check if it's a temp message
    const message = messages.find(m => m._id === messageId);
    
    // Prevent deleting temp messages that are still being sent
    if (message?.isTemp || messageId?.startsWith('temp_') || message?.status?.sending) {
      toast.error("Please wait for the message to be delivered before deleting");
      return;
    }
    
    try {
      const response = await messagingService.deleteMessage(messageId);

      if (response.success) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId
              ? { ...msg, isDeleted: true, content: null, type: "text" }
              : msg
          )
        );
        toast.success("Message deleted successfully");
      }
    } catch (error) {
toast.error(error.message || "Failed to delete message");
    }
  };

  const handleReactToMessage = async (messageId, emoji) => {
    try {
      const currentUserId = user?.id || user?._id;
      const message = messages.find((m) => m._id === messageId);
      const existingReaction = message?.reactions?.find(
        (r) => r.user === currentUserId && r.emoji === emoji
      );

      if (existingReaction) {
        await messagingService.removeReaction(messageId, emoji);
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg._id === messageId) {
              return {
                ...msg,
                reactions: msg.reactions.filter(
                  (r) => !(r.user === currentUserId && r.emoji === emoji)
                ),
              };
            }
            return msg;
          })
        );
      } else {
        await messagingService.addReaction(messageId, emoji);
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg._id === messageId) {
              return {
                ...msg,
                reactions: [
                  ...(msg.reactions || []),
                  {
                    emoji,
                    user: currentUserId,
                    createdAt: new Date().toISOString(),
                  },
                ],
              };
            }
            return msg;
          })
        );
      }
    } catch (error) {
toast.error(error.message || "Failed to add reaction");
    }
  };

  const handleReportMessage = (messageId) => {
    setReportingMessageId(messageId);
    setShowReportModal(true);
  };

  const handleSubmitReport = async (messageId, reportData) => {
    try {
      const response = await messagingService.reportMessage(
        messageId,
        reportData
      );
      if (response.success) {
        toast.success("Message reported successfully.");
        setShowReportModal(false);
        setReportingMessageId(null);
      }
    } catch (error) {
toast.error(error.message || "Failed to report message");
    }
  };

  // Filter conversations
  const filteredConversations = useMemo(() => {
    const uniqueConversations = conversations.reduce((acc, conv) => {
      if (!acc.find((c) => c._id === conv._id)) {
        acc.push(conv);
      }
      return acc;
    }, []);

    return uniqueConversations.filter((conv) => {
      const otherParticipant = conv.otherParticipant;
      // Handle empty search query - should match all conversations
      // Also handle null/undefined names gracefully
      const matchesSearch = !searchQuery.trim()
        ? true
        : (otherParticipant?.name || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());

      if (conversationFilter === "all") return matchesSearch;
      if (conversationFilter === "online") {
        return matchesSearch && otherParticipant?.isOnline;
      }
      if (conversationFilter === "unread") {
        return matchesSearch && (unreadByConversation[conv._id] || 0) > 0;
      }
      return matchesSearch;
    });
  }, [conversations, searchQuery, conversationFilter, unreadByConversation]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="msgx-container">
      {/* SIDEBAR */}
      <ChatSidebar
        conversations={filteredConversations}
        selectedConversation={selectedConversation}
        onConversationSelect={handleConversationSelect}
        onNewMessage={handleNewMessageClick}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        conversationFilter={conversationFilter}
        onFilterChange={setConversationFilter}
        totalUnread={totalUnread}
        unreadByConversation={unreadByConversation}
        typingUsers={typingUsers}
        loading={loading}
        isHidden={!!selectedConversation}
      />

      {/* NEW MESSAGE MODAL */}
      <NewMessageModal
        isOpen={showNewMessageModal}
        onClose={handleNewMessageModalClose}
        onSelectContact={handleSelectContactFromModal}
      />

      {/* CHAT AREA */}
      <div className={`msgx-chat-area ${selectedConversation ? "active" : ""}`}>
        {selectedConversation ? (
          <>
            {/* Header */}
            <ChatHeader
              conversation={selectedConversation}
              onBack={() => setSelectedConversation(null)}
              isE2EEEnabled={isE2EEEnabled}
              encryptionReady={encryptionReady}
              onSearchClick={() => setShowSearchModal(true)}
            />

            {/* Jumped Mode Indicator */}
            {isJumpedMode && (
              <motion.div
                className="msgx-jumped-mode-banner"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                style={{
                  background: "#3b82f6",
                  color: "white",
                  padding: "12px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                <span>📍 Viewing message context - Scroll up/down for more</span>
                <button
                  onClick={handleExitJumpedMode}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "13px",
                  }}
                >
                  Return to Latest
                </button>
              </motion.div>
            )}

            {/* Messages */}
            <motion.div
              className="msgx-messages-wrapper"
              ref={messagesContainerRef}
              onScroll={handleScroll}
              variants={messagesContainerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Loading overlay during jump */}
              {isJumping && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 100,
                  backdropFilter: 'blur(4px)'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div className="msgx-spinner" />
                    <span style={{ display: 'block', marginTop: '12px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                      Loading message...
                    </span>
                  </div>
                </div>
              )}
              {/* Load More Indicator - ⚠️ Use pagination.isLoadingMore */}
              {pagination.isLoadingMore && (
                <div className="msgx-loading">
                  <div className="msgx-spinner" />
                  <span className="msgx-loading-text">
                    Loading older messages...
                  </span>
                </div>
              )}

              {/* Load More Button - Different logic for jumped vs normal mode */}
              {!pagination.isLoadingMore && !jumpLoadingOlder && (
                <>
                  {/* Normal Mode: Show if has more messages */}
                  {!isJumpedMode && pagination.hasNext && pagination.nextCursor && (
                    <button
                      onClick={loadMoreMessages}
                      className="msgx-load-more-btn"
                      style={{
                        alignSelf: "center",
                        padding: "8px 16px",
                        margin: "12px 0",
                        background: "transparent",
                        border: "1px solid var(--border-color, #ddd)",
                        borderRadius: "20px",
                        color: "var(--text-secondary, #666)",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      ↑ Load older messages
                    </button>
                  )}

                  {/* Jumped Mode: Show if has older messages */}
                  {isJumpedMode && jumpHasOlder && (
                    <button
                      onClick={loadOlderJumped}
                      className="msgx-load-more-btn"
                      style={{
                        alignSelf: "center",
                        padding: "8px 16px",
                        margin: "12px 0",
                        background: "transparent",
                        border: "1px solid var(--border-color, #ddd)",
                        borderRadius: "20px",
                        color: "var(--text-secondary, #666)",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      ↑ Load older messages (jumped mode)
                    </button>
                  )}
                </>
              )}

              {/* Loading indicator at TOP (when loading older messages in jumped mode) */}
              {isJumpedMode && jumpLoadingOlder && (
                <div className="msgx-loading" style={{ margin: "12px 0" }}>
                  <div className="msgx-spinner" />
                  <span className="msgx-loading-text">Loading older messages...</span>
                </div>
              )}

              {loadingMessages && !messages.length ? (
                <div className="msgx-loading">
                  <div className="msgx-spinner" />
                  <span className="msgx-loading-text">Loading messages...</span>
                </div>
              ) : error ? (
                <div className="msgx-loading">
                  <span className="msgx-loading-text">{error}</span>
                </div>
              ) : (
                <AnimatePresence>
                  {/* ⚠️ CRITICAL: Use jumpedMessages in jumped mode, regular messages otherwise */}
                  {groupMessagesByDate(isJumpedMode ? jumpedMessages : messages).map((group) => (
                    <div key={group.dateKey} className="msgx-date-group">
                      {/* Date Separator Header */}
                      <div className="msgx-date-separator">
                        <div className="msgx-date-line" />
                        <span>{group.label}</span>
                        <div className="msgx-date-line" />
                      </div>

                      {/* Messages for this date */}
                      {group.messages.map((message, index) => {
                        const isMine = message.senderId === "me";
                        // ⚠️ Use helper function from guide
                        const showAvatar =
                          !isMine && shouldShowAvatar(group.messages, index);
                        const isDeleted = message.isDeleted || !message.content;

                        // Check if this is the highlighted message
                        const isHighlighted = highlightedMessageId === message._id;

                        return (
                          <Message
                            key={message._id}
                            message={message}
                            onReply={() => handleReplyToMessage(message)}
                            onShowMenu={() =>
                              handleShowMenuViaGesture(message._id)
                            }
                            className={isMine ? "sent" : "received"}
                            data-message-id={message._id}
                            style={{
                              transition: "all 0.3s ease",
                              ...(isHighlighted
                                ? {
                                    backgroundColor: "rgba(59, 130, 246, 0.2)",
                                    border: "2px solid rgba(59, 130, 246, 0.5)",
                                    borderRadius: "12px",
                                    padding: "8px",
                                    margin: "4px 0",
                                    boxShadow: "0 0 20px rgba(59, 130, 246, 0.3)",
                                    animation: "pulse 2s ease-in-out infinite",
                                  }
                                : {}),
                            }}
                          >
                            <MessageBubble
                              message={message}
                              isMine={isMine}
                              showAvatar={showAvatar}
                              otherParticipant={
                                selectedConversation.otherParticipant
                              }
                              currentUserId={user?.id || user?._id}
                              onReply={handleReplyToMessage}
                              onReact={handleReactToMessage}
                              editingMessageId={editingMessageId}
                              editContent={editContent}
                              setEditContent={setEditContent}
                              onCancelEdit={handleCancelEdit}
                              onSubmitEdit={handleSubmitEdit}
                            />

                            {/* Message Menu */}
                            {!isDeleted && editingMessageId !== message._id && (
                              <MessageMenu
                                message={message}
                                isMyMessage={isMine}
                                onReply={handleReplyToMessage}
                                onEdit={handleEditMessage}
                                onDelete={handleDeleteMessage}
                                onReport={handleReportMessage}
                                onReact={handleReactToMessage}
                                forceShowMenu={
                                  longPressMenuMessageId === message._id
                                }
                                onMenuClose={() =>
                                  setLongPressMenuMessageId(null)
                                }
                              />
                            )}
                          </Message>
                        );
                      })}
                    </div>
                  ))}

                  {/* Loading indicator at BOTTOM (when loading newer messages in jumped mode) */}
                  {isJumpedMode && jumpLoadingNewer && (
                    <div className="msgx-loading" style={{ margin: "12px 0" }}>
                      <div className="msgx-spinner" />
                      <span className="msgx-loading-text">Loading newer messages...</span>
                    </div>
                  )}

                  {/* Typing Indicator */}
                  {typingUsers.size > 0 && (
                    <motion.div
                      className="msgx-typing-indicator"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                    >
                      {selectedConversation.otherParticipant
                        ?.profilePhotoUrl ? (
                        <img
                          src={
                            selectedConversation.otherParticipant
                              .profilePhotoUrl
                          }
                          alt=""
                          className="msgx-message-avatar"
                        />
                      ) : (
                        <div className="msgx-message-avatar-initials">
                          {selectedConversation.otherParticipant?.name
                            ?.charAt(0)
                            .toUpperCase() || "?"}
                        </div>
                      )}
                      <div className="msgx-typing-bubble">
                        <span className="msgx-typing-dot" />
                        <span className="msgx-typing-dot" />
                        <span className="msgx-typing-dot" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              <div ref={messagesEndRef} />
            </motion.div>

            {/* Input */}
            <ChatInput
              ref={messageInputRef}
              newMessage={newMessage}
              onMessageChange={handleInputChange}
              onSendMessage={handleSendMessage}
              onEmojiToggle={() => setShowEmojiPicker(!showEmojiPicker)}
              replyingTo={replyingTo}
              onCancelReply={cancelReply}
              onTypingStop={handleTypingStop}
              isE2EEEnabled={isE2EEEnabled}
              encryptionReady={encryptionReady}
              currentUser={user}
              otherParticipant={selectedConversation?.otherParticipant}
            />

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <motion.div
                className="msgx-emoji-picker"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
              >
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  searchDisabled
                  width={300}
                  height={350}
                />
              </motion.div>
            )}
          </>
        ) : (
          <motion.div
            className="msgx-empty-state"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="msgx-empty-icon">
              <FiMessageCircle />
            </div>
            <h3 className="msgx-empty-title">No Conversation Selected</h3>
            <p className="msgx-empty-text">
              Select a conversation from the list to start messaging
            </p>
            <div className="msgx-empty-hint">
              <p>
                💡 <strong>Note:</strong> You can only message your followers,
                people you follow, or collaborators
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportingMessageId(null);
        }}
        onSubmit={handleSubmitReport}
        messageId={reportingMessageId}
      />

      {/* Search Modal */}
      <MessageSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onJumpToMessage={handleJumpToMessage}
        conversationId={selectedConversation?._id}
        conversationName={selectedConversation?.otherParticipant?.name}
      />
    </div>
  );
};

export default MessagingCenter;
